import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { st } from "../../styles.js";
import { BuzerIcon } from "../../BuzerIcons";
import { pad2 } from "../../lib/format";
import { useWakeLock } from "../../hooks/useWakeLock";

export function Timer({ id, seconds, onComplete, variant }) {
  const key = "buzer_timer_" + id;
  const [endTime, setEndTime] = useState(() => {
    const v = localStorage.getItem(key);
    return v ? parseInt(v, 10) : null;
  });
  const [now, setNow] = useState(() => Date.now());
  const [full, setFull] = useState(false);  // H14: fullscreen countdown modal
  const acRef = useRef(null);
  const lastBeep = useRef(0);
  useWakeLock(!!endTime);  // kým časovač beží, obrazovka nezhasne (a meranie sa dá zastaviť)
  useEffect(() => {
    if (!endTime) return;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [endTime]);
  useEffect(() => {
    if (endTime && now >= endTime) {
      localStorage.removeItem(key);
      setEndTime(null); setFull(false);
      beep(990, 0.4);  // final tone
      if (onComplete) onComplete();
    }
  }, [now, endTime]);  // eslint-disable-line
  // H14: audible countdown in the last 5 seconds (one beep per second, no asset)
  useEffect(() => {
    if (!endTime) return;
    const rem = Math.ceil((endTime - now) / 1000);
    if (rem <= 5 && rem >= 1 && lastBeep.current !== rem) { lastBeep.current = rem; beep(700, 0.18); }
  }, [now, endTime]);  // eslint-disable-line
  function beep(freq, dur) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!acRef.current) acRef.current = new AC();
      const ac = acRef.current;
      const o = ac.createOscillator(); const g = ac.createGain();
      o.frequency.value = freq; o.type = "sine"; o.connect(g); g.connect(ac.destination);
      g.gain.setValueAtTime(0.0001, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.32, ac.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
      o.start(); o.stop(ac.currentTime + dur + 0.02);
    } catch (e) { /* audio blocked — silent */ }
  }
  function start() {
    // resume/unlock audio within the user gesture so the final-5s beeps can play
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) { if (!acRef.current) acRef.current = new AC(); acRef.current.resume && acRef.current.resume(); }
    } catch (e) { /* */ }
    const e = Date.now() + seconds * 1000;
    localStorage.setItem(key, String(e));
    setNow(Date.now());
    setEndTime(e);
  }
  function stop() { localStorage.removeItem(key); setEndTime(null); setFull(false); }
  const lbl = seconds < 60 ? seconds + "s"
    : (seconds % 60 === 0 ? (seconds / 60) + "′" : Math.floor(seconds / 60) + ":" + pad2(seconds % 60));
  const clockLbl = Math.floor(seconds / 60) + ":" + pad2(seconds % 60);  // M:SS for the button variant
  if (!endTime) {
    // B2: first click both starts the timer and opens the fullscreen view
    if (variant === "button")
      return <button onClick={() => { start(); setFull(true); }} className="bz-btn bz-btn--primary bz-btn--block" title="Spustiť časovač"><BuzerIcon name="clock" size={18} color="currentColor" />Spustiť časovač · {clockLbl}</button>;
    return <button onClick={() => { start(); setFull(true); }} style={{ ...st.iconBtn, fontFamily: "var(--font-mono)", fontWeight: 700 }} title={"Spustiť časovač"}><BuzerIcon name="stopwatch" size={15} style={{ verticalAlign: "-2px", marginRight: 4 }} />{lbl}</button>;
  }
  const rem = Math.max(0, Math.ceil((endTime - now) / 1000));
  const clock = Math.floor(rem / 60) + ":" + pad2(rem % 60);
  return (
    <>
      {variant === "button"
        ? <button onClick={() => setFull(true)} className="bz-btn bz-btn--secondary bz-btn--block" title="Časovač"><BuzerIcon name="clock" size={18} color="currentColor" />Časovač · {clock}</button>
        : <button onClick={() => setFull(true)} style={{ ...st.iconBtn, ...st.timerOn, fontFamily: "var(--font-mono)", fontWeight: 700 }} title="Časovač — otvoriť na celú obrazovku"><BuzerIcon name="stopwatch" size={15} style={{ verticalAlign: "-2px", marginRight: 4 }} />{clock}</button>}
      {full && createPortal(
        <div style={st.timerFull} onClick={() => setFull(false)}>
          <div style={st.timerFullInner} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...st.timerDigits, fontFamily: "var(--font-mono)", color: rem <= 5 ? "var(--ember)" : "var(--text)" }}>{clock}</div>
            <div style={{ ...st.timerFullHint, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{rem <= 5 ? <>Skoro hotovo! <BuzerIcon name="flame" size={16} color="currentColor" /></> : <>Sústreď sa <BuzerIcon name="dumbbell" size={16} color="currentColor" /></>}</div>
            <div style={{ display: "flex", gap: 10, marginTop: 22, width: "100%", maxWidth: 300 }}>
              <button onClick={stop} style={st.ghostBtn}>Zastaviť</button>
              <button onClick={() => setFull(false)} style={{ ...st.primaryBtn, flex: 1 }}>Skryť</button>
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
}
